import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Breadcrumbs,
  Link,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import axios from '../api/axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { PageRenderer } from '../components/PageRenderer';
import DecorativeCubes from '../components/DecorativeCubes';

const PageView = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`/pages/slug/${slug}`);
        setPage(response.data);
        
        // Устанавливаем SEO метаданные
        document.title = response.data.title + ' - Волшебная страна';
        if (response.data.description) {
          const metaDescription = document.querySelector('meta[name="description"]');
          if (metaDescription) {
            metaDescription.setAttribute('content', response.data.description);
          }
        }
      } catch (err) {
        console.error('Ошибка загрузки страницы:', err);
        setError('Страница не найдена');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [slug]);

  if (loading) {
    return (
      <>
        <Header />
        <Container>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <CircularProgress />
          </Box>
        </Container>
        <Footer />
      </>
    );
  }

  if (error || !page) {
    return (
      <>
        <Header />
        <Container>
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error || 'Страница не найдена'}
            </Alert>
            <Button variant="contained" onClick={() => navigate('/')}>
              Вернуться на главную
            </Button>
          </Box>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <DecorativeCubes minCubes={6} maxCubes={11} />
      <Header />
      <Container sx={{ py: 4, position: 'relative', zIndex: 2 }}>
        {/* Хлебные крошки */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <Link
            component="button"
            onClick={() => navigate('/')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              textDecoration: 'none',
              color: 'text.primary',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Главная
          </Link>
          <Typography color="text.primary">{page.title}</Typography>
        </Breadcrumbs>

        {/* Содержание страницы */}
        <Paper elevation={2} sx={{ p: { xs: 3, md: 5 } }}>
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              mb: 3,
              fontSize: { xs: '2rem', md: '3rem' }
            }}
          >
            {page.title}
          </Typography>

          {/* Rich content */}
          <Box className="page-content">
            <PageRenderer
              content={page.content}
              editorType={page.editorType}
              blocks={page.blocks}
            />
          </Box>

          {/* Дата обновления */}
          {page.updatedAt && (
            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="caption" color="text.secondary">
                Последнее обновление: {new Date(page.updatedAt).toLocaleDateString('ru-RU', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>
      <Footer />
    </Box>
  );
};

export default PageView;
